/* eslint-disable */
import { factoryMatches } from './factoryMatch';
import { SELECTION_SEASON, readSelectionAt } from '../components/developments/SelectionChip';
import { devIsMovedToOrder, LOCK_KEY } from './devLock';

// Single source of truth for the dev "status" filter (the 7-pill strip + the
// Status dropdown). The Set carries a mix of stage keys + special pseudo-keys:
//
//   stage keys:    'Drawing stage' · 'Hand made sample stage' · 'Color sample stage'
//                  '_moved_to_order' · '_done' · '_stopped'  (Collection C2: a locked
//                  development is FIRST — above done AND stopped, D1 — via devLock.js)
//   special keys:  '_no_image' · '_imported'
//
// Stage and special groups are independent — a Set with both must match
// at least one entry from each group (AND between groups, OR within).
// Used by useDevFilters (grid filter) and DevStatusBar (pill counts +
// "compute ignoring this pill" math).

const STAGE_KEYS_ONLY = new Set([
  'Drawing stage',
  'Hand made sample stage',
  'Color sample stage',
  LOCK_KEY,
  '_done',
  '_stopped',
]);

// Brief P — unified Done + Completed detection. Aligned with the counts RPC at
// sql/dev-stage-counts-rpc-03-canonical-v2.sql (Brief I.5): the _done bucket
// matches `stop_reason='completed' OR stage_status='Done' OR completedAt
// IS NOT NULL`, plus the legacy full_status text patterns kept for historical
// rows that lack any of the three structured markers. Sibling helper at
// src/utils/devCardUtils.js isCompleted must stay in lockstep (Convention
// #122 — single canonical detection across surfaces). Drives:
//   - Status pill counts + dropdown (DevStatusBar)
//   - Grid filtering (useDevFilters via matchesStatusSet)
//   - Board column grouping (DevelopmentsTab via devEffectiveStage)
export function isDevDone(d) {
  if (!d) return false;
  const fs = (d.full_status || d.data?.full_status || '').toLowerCase();
  const stage = (d.stage_status || d.data?.stage_status || '').trim();
  const completedAt = d.completedAt || d.data?.completedAt;
  const stopReason = d.stop_reason || d.data?.stop_reason;
  return (
    stopReason === 'completed' ||
    stage === 'Done' ||
    completedAt != null ||
    ((fs.includes('color samples confirmed') || fs.includes('done')) &&
      !fs.startsWith('stopped') &&
      fs !== 'on hold')
  );
}

export function isDevStopped(d) {
  const fs = (d.full_status || d.data?.full_status || '').toLowerCase();
  const stopReason = d.stop_reason || d.data?.stop_reason;
  return stopReason === 'paused' || stopReason === 'cancelled' || fs.startsWith('stopped');
}

export function devHasNoImage(d) {
  const imgs = d.images || d.data?.images || [];
  return !imgs.length;
}

export function devIsImported(d) {
  return !!(d.imported || d.data?.imported);
}

export function devStageBucket(d) {
  if (devIsMovedToOrder(d)) return LOCK_KEY;   // mirrors _dev_stage_bucket + get_dev_stage_counts v-next
  if (isDevStopped(d)) return '_stopped';
  if (isDevDone(d)) return '_done';
  const v = (d.stage_status || d.data?.stage_status || '').trim();
  if (
    v === 'Drawing stage' ||
    v === 'Hand made sample stage' ||
    v === 'Color sample stage'
  ) {
    return v;
  }
  return null;
}

// Returns true if the dev passes the given status Set. Empty/missing Set → true.
// Mirrors the inline branch that previously lived in useDevFilters.
export function matchesStatusSet(d, statusSet) {
  if (!statusSet || statusSet.size === 0) return true;
  const stageVal = (d.stage_status || d.data?.stage_status || '').trim();
  const isMoved = devIsMovedToOrder(d);
  const isDone = isDevDone(d);
  const isStopped = isDevStopped(d);
  const noImg = devHasNoImage(d);
  const imp = devIsImported(d);
  const noImgMatches = statusSet.has('_no_image') && noImg;
  const importMatches = statusSet.has('_imported') && imp;
  // A locked development matches ONLY the lock key — never Done, Stopped or its frozen stage.
  const movedMatches = statusSet.has(LOCK_KEY) && isMoved;
  const stageMatches = statusSet.has(stageVal) && !isDone && !isStopped && !isMoved;
  const doneMatches = statusSet.has('_done') && isDone && !isStopped && !isMoved;
  const stoppedMatches = statusSet.has('_stopped') && isStopped && !isMoved;
  let hasStageFilters = false;
  let hasSpecialFilters = false;
  for (const k of statusSet) {
    if (k === '_no_image' || k === '_imported') hasSpecialFilters = true;
    else hasStageFilters = true;
  }
  if (hasStageFilters && !stageMatches && !doneMatches && !stoppedMatches && !movedMatches) return false;
  if (hasSpecialFilters && !noImgMatches && !importMatches) return false;
  if (!hasStageFilters && !hasSpecialFilters) return false;
  return true;
}

// Effective stage_status — top-level first, data.* fallback, trimmed.
// Matches the canonical expression on matchesStatusSet line 65 byte-for-byte
// so consumers (e.g. Board column grouping) stay in lockstep with the SSOT.
export function devEffectiveStage(d) {
  return (d.stage_status || d.data?.stage_status || '').trim();
}

// Helpers for "ignoring this pill" math used by DevStatusBar:
// strip stage entries (keep special) → for stage-pill counts.
// strip a single key → for No Image / Imported pills.
export function stripStageEntries(statusSet) {
  const out = new Set();
  if (statusSet) {
    for (const k of statusSet) {
      if (!STAGE_KEYS_ONLY.has(k)) out.add(k);
    }
  }
  return out;
}

export function stripStatusEntry(statusSet, key) {
  const out = new Set(statusSet || []);
  out.delete(key);
  return out;
}

/**
 * matchesSunOptic — Sun/Optic filter predicate (Brief F.1)
 *
 * Sun/Optic is a DERIVED filter computed from the existing `dev_type` field.
 * The field lives at `dev.dev_type` (top-level after flattenDev in
 * useDevsPaginated.js:121) or `dev.data?.dev_type` (raw row). Mapping per
 * locked α decision:
 *   - dev_type === 'DA' → Sun bucket
 *   - dev_type === 'DS' → Sun bucket
 *   - dev_type === 'DO' → Optic bucket
 *   - dev_type === 'CL' → EXCLUDED when any Sun/Optic filter is active
 *
 * When the user selects "Sun" only:   matches DA + DS, excludes DO + CL.
 * When the user selects "Optic" only: matches DO, excludes DA + DS + CL.
 * When the user selects both:         matches DA + DS + DO, still excludes CL.
 * When the user selects neither (empty Set): predicate returns TRUE for
 *   all devs (filter inactive; pass-through).
 *
 * @param {object} dev - The dev record (top-level fields preferred,
 *                       fallback to dev.data nested)
 * @param {Set<'sun' | 'optic'>} selectedSet - The selected Sun/Optic values
 * @returns {boolean}
 */
export function matchesSunOptic(dev, selectedSet) {
  // Empty Set = filter inactive, pass-through
  if (!selectedSet || selectedSet.size === 0) return true;

  const type = (dev?.dev_type || dev?.data?.dev_type || '').trim();
  if (!type) return false; // unknown type — exclude when filter active

  const wantsSun   = selectedSet.has('sun');
  const wantsOptic = selectedSet.has('optic');

  // DA + DS → Sun bucket
  if (type === 'DA' || type === 'DS') return wantsSun;
  // DO → Optic bucket
  if (type === 'DO') return wantsOptic;
  // CL is always excluded when filter is active (α decision)
  return false;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Shared value-set predicates (Convention #61 — one definition, many surfaces).

   These four were inline closures inside useDevFilters (_setMatch, _factoryMatch,
   _seasonMatch, and the decision/procurement block). They were LIFTED here — not
   copied — when the designer card grew its own filter bar, so the Board and the
   designer card cannot drift into disagreeing about the same development.
   useDevFilters now imports them; it holds no second copy.

   Every one is pass-through on an empty/absent selection, so an inactive axis
   never narrows anything.
   ──────────────────────────────────────────────────────────────────────────── */

/** Generic exact-value membership. Accepts a Set (external filters). */
export function matchesValueSet(set, val) {
  if (!set || set.size === 0) return true;
  return set.has((val || '').trim());
}

/** Generic exact-value membership for the ARRAY-shaped internal advanced filters. */
export function matchesValueList(arr, val) {
  if (!Array.isArray(arr) || arr.length === 0) return true;
  return arr.includes((val || '').trim());
}

/**
 * Factory match by canonical CODE, so an old-format dev ("IP") still surfaces
 * when a new-format label ("IP — Inter") is picked, and vice-versa
 * (Convention #145 — the fty dual-format is a DATA inconsistency, absorbed here).
 * Accepts a Set (external filter) or an Array (internal advanced filter).
 */
export function matchesFactorySet(selected, val) {
  if (!selected) return true;
  const entries = selected instanceof Set
    ? [...selected]
    : (Array.isArray(selected) ? selected : null);
  if (!entries || entries.length === 0) return true;
  return entries.some((s) => factoryMatches(val, s));
}

/**
 * Season match. The '_no_season' sentinel matches devs with an empty season;
 * every other entry is exact membership on the full production string
 * (never the bare shared constant — see deriveSeasonOptions / Conv #145).
 */
export function matchesSeasonSet(set, val) {
  if (!set || set.size === 0) return true;
  const v = (val || '').trim();
  return v === '' ? set.has('_no_season') : set.has(v);
}

/**
 * Selection Decision + Procurement, read from the FIXED selection season so the
 * client agrees exactly with the server predicate in useDevsPaginated.
 * A dev with no decision for that season is excluded when either group is active.
 */
export function matchesSelectionSets(dev, decSet, procSet) {
  const decOn = decSet && decSet.size > 0;
  const procOn = procSet && procSet.size > 0;
  if (!decOn && !procOn) return true;

  const sel = dev?.selections || dev?.data?.selections;
  // A JSON-null or array-shaped mirror is not a decision map — refuse it rather
  // than index into it (the array shape returns a numeric index, never a season).
  if (!sel || typeof sel !== 'object' || Array.isArray(sel)) return false;
  // readSelectionAt is the SAME tolerant read the chip uses, so a row can never
  // render one way and filter another. It resolves the two axes separately and
  // falls back to the legacy encoding where `decision` carried order/preorder.
  const r = readSelectionAt(sel[SELECTION_SEASON]);
  if (!r) return false;

  // The axes are independent now: a dev may be ordered with no meeting decision
  // (procurement set, decision null) or decided with nothing ordered.
  if (decOn) {
    if (!r.decision) return false;
    const inSel = r.decision !== 'out';
    if (!((decSet.has('in') && inSel) || (decSet.has('out') && r.decision === 'out'))) return false;
  }
  if (procOn && !(r.procurement && procSet.has(r.procurement))) return false;
  return true;
}

/**
 * Time-in-current-stage bands. NEW axis — the Board has no equivalent, so it is
 * defined here (the single source of truth) rather than in a designer-local
 * helper, so a future Board "Waiting" pill inherits the same bands.
 *
 * `days` comes from daysWaiting() (delay-report semantics). A DONE development
 * is not waiting for anything, so it is excluded whenever the axis is active;
 * so is a dev with no resolvable start date (days == null).
 */
export const WAITING_BANDS = [
  { key: '0-30',   label: 'Under 30 days', test: (d) => d < 30 },
  { key: '30-90',  label: '30 – 90 days',  test: (d) => d >= 30 && d < 90 },
  { key: '90-180', label: '90 – 180 days', test: (d) => d >= 90 && d < 180 },
  { key: '180+',   label: 'Over 180 days', test: (d) => d >= 180 },
];

/** Band key for a dev, or null when it cannot be placed (done / no start date). */
export function waitingBandOf(dev, days) {
  if (isDevDone(dev) || devIsMovedToOrder(dev)) return null;   // a locked development is not waiting
  if (days == null) return null;
  const b = WAITING_BANDS.find((x) => x.test(days));
  return b ? b.key : null;
}
